import { useContext, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Modal } from "react-bootstrap";
import { DataContext } from "../DataContext";
import { Button, VerifyEmail, AccountTemplate, Wrapper, PageTemplate, HeaderTemplate, ButtonLink } from "../styles";
import { changePassword } from "../api/user";
import { PasswordTemplate } from "../templates/PasswordTemplate";
import { ProductsTemplate } from "../templates/ProductsTemplate";
import { ViewReturnsTemplate } from "../templates/ViewReturnsTemplate";
import { convertDate, hasReturnWindowExpired } from "../utils";
import { useNavigate } from "react-router-dom";
import { updateStatus } from "../api/order";
import toast from "react-hot-toast";

const Customer = () => {
  const { user, logout, isLoading, isAuthenticated } = useAuth0();
  const data = useContext(DataContext);

  const { customer } = data;

  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [modalTemplate, setModalTemplate] = useState("");
  const [currentOrder, setCurrentOrder] = useState(null);
  const [showReturnsModal, setShowReturnsModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const handleCloseModal = () => setShowModal(false);
  const handleShowModal = () => setShowModal(true);

  // component mount
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      data.getCustomer();
    }
  }, []);

  const handlePasswordChange = () => {
    changePassword(user);
    setModalTemplate("password");
    handleShowModal();
  };

  const handleViewProducts = (order) => {
    setCurrentOrder(order);
    setModalTemplate("products");
    handleShowModal();
  };

  const handleViewReturns = (order) => {
    setCurrentOrder(order);
    setShowReturnsModal(true);
  };

  const handleLogout = () => {
    data.clearCartState();
    sessionStorage.removeItem("login-time");
    logout();
  };

  const removeDash = (status) => {
    return status.replace(/-/g, " ");
  };

  const getPaymentStatus = (order) => {
    if (["pending", "paid"].includes(order.paymentStatus)) return order.paymentStatus;
    if (order.status === "pending") return "pending";
    return "paid";
  };

  const getFulfillmentStatus = (order) => {
    if (["pending", "in-progress", "shipped", "completed", "cancelled"].includes(order.fulfillmentStatus)) return order.fulfillmentStatus;
    if (order.fulfillmentStatus === "returned") return "completed";
    if (["in-progress", "shipped", "completed", "cancelled"].includes(order.status)) return order.status;
    if (order.status === "return") return "completed";
    return "pending";
  };

  const handleShowCancelModal = (order) => {
    setCurrentOrder(order);
    setShowCancelModal(true);
  };

  const handleOrderCancellation = async () => {
    try {
      await updateStatus({
        orderNumber: currentOrder.orderNumber,
        fulfillmentStatus: "cancelled",
      });
      setShowCancelModal(false);
      // only get customer if logged in
      if (isAuthenticated) {
        data.getCustomer();
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message);
    }
  };

  if (!customer || !user) return null;

  return user.email_verified ? (
    <>
      <AccountTemplate className={`account-template narrow ${customer.orders.length === 0 && "no-orders"}`}>
        <div className="account-flex-wrapper">
          <div className="order-history">
            {customer.orders.length > 0 ? (
              customer.orders.map((order) => {
                const pricingProductsForReturn = () => {
                  if (order.returns.length === 0) return true;
                  return order.returns[order.returns.length - 1].products.find((product) => {
                    return product.returnType === "Keep";
                  });
                };

                const returnWindowExpired = hasReturnWindowExpired(order.updatedAt);
                const paymentStatus = getPaymentStatus(order);
                const fulfillmentStatus = getFulfillmentStatus(order);

                const showCreateReturnButton =
                  !returnWindowExpired &&
                  fulfillmentStatus === "completed" &&
                  pricingProductsForReturn();

                return (
                  <div className="order-history-row product-row" key={order.orderNumber}>
                    <div className="order-history-details product-block">
                      <div className="product-block-row off-white">
                        <div className="product-block-label">Order #</div>
                        <div className="product-block-value">{order.orderNumber}</div>
                      </div>
                      <div className="product-block-row off-white">
                        <div className="product-block-label">Date</div>
                        <div className="product-block-value">{convertDate(order.createdAt)}</div>
                      </div>
                      <div className="product-block-row off-white">
                        <div className="product-block-label">Payment</div>
                        <div className={`product-block-value status ${paymentStatus.toLowerCase()}`}>
                          <span>{removeDash(paymentStatus)}</span>
                        </div>
                      </div>
                      <div className="product-block-row off-white">
                        <div className="product-block-label">Fulfillment</div>
                        <div className={`product-block-value status ${fulfillmentStatus.toLowerCase()}`}>
                          <span>{removeDash(fulfillmentStatus)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="order-history-buttons">
                      <Button
                        className="order-history-link clear"
                        onClick={() => navigate("/support", { state: { orderNumber: order.orderNumber, previousUrl: "/account" } })}
                      >
                        Support
                      </Button>
                      <Button className="order-history-link clear" onClick={() => handleViewProducts(order)}>
                        View Details
                      </Button>
                      {paymentStatus === "paid" && fulfillmentStatus === "pending" && (
                        <Button className="order-history-link clear" onClick={() => handleShowCancelModal(order)}>
                          Cancel Order
                        </Button>
                      )}
                      {order.tracking && fulfillmentStatus === "shipped" && (
                        <a
                          className="order-history-link clear"
                          href={`https://tools.usps.com/go/TrackConfirmAction?tLabels=${order.tracking}`}
                          target="_blank"
                        >
                          Track Package
                        </a>
                      )}
                      {showCreateReturnButton && (
                        <Button className="order-history-link clear" onClick={() => navigate("/return", { state: { order } })}>
                          Create Return
                        </Button>
                      )}
                      {order.returns?.length > 0 && (
                        <Button className="order-history-link clear" onClick={() => handleViewReturns(order)}>
                          View Returns
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <>
                <h1>Ready to place your first order?</h1>
                <p>Here's some inspiration.</p>
              </>
            )}
          </div>
          <div className="account-info">
            <div>
              <div className="name">{customer.customer.name}</div>
              <div className="email">{customer.customer.email}</div>
            </div>
            {!isLoading && isAuthenticated && (
              <div className="account-buttons">
                <Button className="small grey" onClick={handlePasswordChange}>
                  Change Password
                </Button>
                <Button className="logout grey small" onClick={handleLogout}>
                  Log Out
                </Button>
              </div>
            )}
          </div>
        </div>

        <Modal
          size={modalTemplate === "password" ? "md" : "lg"}
          dialogClassName={modalTemplate === "products" ? "modal-80w" : ""}
          fullscreen={"md-down"}
          show={showModal}
          animation={false}
          onHide={handleCloseModal}
        >
          {modalTemplate === "password" ? <PasswordTemplate /> : <ProductsTemplate order={currentOrder} />}
        </Modal>
        <Modal
          size={"lg"}
          fullscreen={"md-down"}
          show={showReturnsModal}
          onHide={() => setShowReturnsModal(false)}
          className="view-returns"
        >
          <ViewReturnsTemplate currentOrder={currentOrder} />
        </Modal>
        <Modal
          size={"sm"}
          fullscreen={"sm-down"}
          show={showCancelModal}
          onHide={() => setShowCancelModal(false)}
          className="view-cancel-modal"
        >
          <Wrapper className="modal-styles cancel-order">
            <Modal.Header closeButton></Modal.Header>
            <Modal.Body>
              <PageTemplate>
                <div className="cancel-modal-content">
                  <span>Are you sure?</span>
                  <Button onClick={() => handleOrderCancellation()}>Cancel Order</Button>
                </div>
              </PageTemplate>
            </Modal.Body>
          </Wrapper>
        </Modal>
      </AccountTemplate>
    </>
  ) : (
    <>
      <HeaderTemplate className="narrow">
        <div className="header">
          <div className="title">
            <h1>Account Information</h1>
          </div>
        </div>
      </HeaderTemplate>
      <PageTemplate className="narrow">
        <div className="content-page">
          <VerifyEmail>
            <h2>Please verify your email to view account information</h2>
            <p>Check your email for a verification link</p>
            <Button className="logout lightgrey" onClick={logout}>
              Log out
            </Button>
          </VerifyEmail>
        </div>
      </PageTemplate>
    </>
  );
};

export default Customer;
